import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TaskCalendar = ({ tasks }) => {
  const [currentMonth] = useState(new Date(2025, 11, 1)); // December 2025
  
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500';
      case 'medium':
        return 'bg-orange-500';
      case 'low':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-500/10';
      case 'in-progress':
        return 'border-orange-500 bg-orange-500/10';
      case 'overdue':
        return 'border-red-500 bg-red-500/10';
      default:
        return 'border-border bg-card';
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date?.getFullYear(), date?.getMonth() + 1, 0)?.getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date?.getFullYear(), date?.getMonth(), 1)?.getDay();
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const monthName = currentMonth?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const getTasksForDay = (day) => {
    const dateStr = `2025-12-${String(day)?.padStart(2, '0')}`;
    return tasks?.filter(task => task?.dueDate === dateStr);
  };

  const renderCalendarDays = () => {
    const days = [];
    const totalCells = Math.ceil((daysInMonth + firstDay) / 7) * 7;

    for (let i = 0; i < totalCells; i++) {
      const day = i - firstDay + 1;
      const isCurrentMonth = day > 0 && day <= daysInMonth;
      const dayTasks = isCurrentMonth ? getTasksForDay(day) : [];
      const isToday = day === 6; // Highlight Dec 6 as today

      days?.push(
        <div
          key={i}
          className={`min-h-24 p-2 border border-border ${
            !isCurrentMonth ? 'bg-muted/30' : 'bg-card'
          } ${isToday ? 'ring-2 ring-primary' : ''}`}
        >
          {isCurrentMonth && (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isToday ? 'text-primary' : 'text-foreground'}`}>
                  {day}
                </span>
                {dayTasks?.length > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                    {dayTasks?.length}
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {dayTasks?.slice(0, 2)?.map((task) => (
                  <div
                    key={task?.id}
                    className={`p-1.5 rounded border ${getStatusColor(task?.status)} cursor-pointer hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <div className={`w-2 h-2 rounded-full ${getPriorityColor(task?.priority)}`} />
                      <p className="text-xs font-medium text-foreground truncate">{task?.title}</p>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{task?.assignedTo}</p>
                  </div>
                ))}
                {dayTasks?.length > 2 && (
                  <button className="w-full text-xs text-primary hover:underline">
                    +{dayTasks?.length - 2} more
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Compliance Calendar</h3>
          <p className="text-sm text-muted-foreground">{monthName}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="ChevronLeft" size={20} />
          </button>
          <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
            Today
          </button>
          <button className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="ChevronRight" size={20} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0 border border-border rounded-lg overflow-hidden">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
          <div
            key={day}
            className="bg-muted/50 p-3 text-center text-sm font-semibold text-foreground border-b border-border"
          >
            {day}
          </div>
        ))}
        {renderCalendarDays()}
      </div>

      <div className="flex items-center gap-4 mt-6 pt-6 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-sm text-muted-foreground">High Priority</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-500" />
          <span className="text-sm text-muted-foreground">Medium Priority</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-sm text-muted-foreground">Low Priority</span>
        </div>
      </div>
    </div>
  );
};

export default TaskCalendar;